import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { hapticLight, hapticMedium } from '@/lib/native/haptics'

interface SwipeConfig {
  /** Minimum distance in pixels to trigger swipe (default: 50) */
  threshold?: number
  /** Maximum vertical movement allowed (default: 100) */
  maxVerticalMovement?: number
  /** Edge width in pixels where swipe can start (default: 30) - ignored if fullScreen is true */
  edgeWidth?: number
  /** Enable swipe back gesture (default: true) */
  enableBack?: boolean
  /** Allow swipe from anywhere on screen, not just left edge (default: false) */
  fullScreen?: boolean
  /** Show parallax effect on page when swiping (default: false) */
  parallax?: boolean
  /** Custom back handler - if not provided, uses router history */
  onBack?: () => void
  /** Routes where swipe should be disabled */
  disabledRoutes?: string[]
}

/**
 * Hook to enable iOS-style swipe navigation gestures
 * Swipe from left edge (or full screen) to go back
 */
export function useSwipeNavigation(config: SwipeConfig = {}) {
  const {
    threshold = 50,
    maxVerticalMovement = 100,
    edgeWidth = 30,
    enableBack = true,
    fullScreen = false,
    parallax = false,
    onBack,
    disabledRoutes = [],
  } = config

  const navigate = useNavigate()
  const location = useLocation()
  
  const touchStartX = useRef<number>(0)
  const touchStartY = useRef<number>(0)
  const touchCurrentX = useRef<number>(0)
  const isSwiping = useRef<boolean>(false)
  const hasPassedThreshold = useRef<boolean>(false)
  const swipeIndicator = useRef<HTMLDivElement | null>(null)
  const pageOverlay = useRef<HTMLDivElement | null>(null)

  // Check if current route should have swipe disabled
  const isDisabled = disabledRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith(route + '/')
  )

  const handleBack = useCallback(() => {
    if (onBack) {
      onBack()
    } else {
      navigate(-1)
    }
    hapticLight()
  }, [navigate, onBack])

  // Create swipe indicator element (iOS-style chevron)
  const createIndicator = useCallback(() => {
    if (swipeIndicator.current) return
    
    const indicator = document.createElement('div')
    indicator.id = 'swipe-back-indicator'
    indicator.innerHTML = `
      <svg width="12" height="20" viewBox="0 0 12 20" fill="none">
        <path d="M10 2L2 10L10 18" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `
    indicator.style.cssText = `
      position: fixed;
      top: 50%;
      left: 4px;
      transform: translateY(-50%) translateX(-20px);
      color: rgba(0, 0, 0, 0.3);
      opacity: 0;
      pointer-events: none;
      z-index: 9999;
      transition: transform 0.15s ease, opacity 0.15s ease;
    `
    document.body.appendChild(indicator)
    swipeIndicator.current = indicator
    
    // Create page overlay for parallax effect
    if (parallax) {
      const overlay = document.createElement('div')
      overlay.id = 'swipe-page-overlay'
      overlay.style.cssText = `
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0);
        pointer-events: none;
        z-index: 9998;
        transition: background 0.1s ease;
      `
      document.body.appendChild(overlay)
      pageOverlay.current = overlay
    }
  }, [parallax])

  const showIndicator = useCallback((progress: number, deltaX: number) => {
    if (!swipeIndicator.current) return
    const clampedProgress = Math.min(1, Math.max(0, progress))
    
    // Animate the chevron: slide in and become more visible
    const translateX = Math.min(deltaX * 0.5, 30) - 20 // Slide from -20 to +10
    swipeIndicator.current.style.opacity = String(Math.min(clampedProgress * 1.5, 1))
    swipeIndicator.current.style.transform = `translateY(-50%) translateX(${translateX}px)`
    swipeIndicator.current.style.transition = 'none'
    
    // Apply parallax effect to page content
    if (parallax && pageOverlay.current) {
      const pageTranslateX = Math.min(deltaX, 100) // Max 100px translate
      document.body.style.transform = `translateX(${pageTranslateX}px)`
      document.body.style.transition = 'none'
      pageOverlay.current.style.background = `rgba(0, 0, 0, ${clampedProgress * 0.2})`
    }
    
    // Haptic feedback when crossing threshold
    if (progress >= 1 && !hasPassedThreshold.current) {
      hasPassedThreshold.current = true
      hapticMedium()
    } else if (progress < 1 && hasPassedThreshold.current) {
      hasPassedThreshold.current = false
      hapticLight()
    }
  }, [parallax])

  const hideIndicator = useCallback(() => {
    if (!swipeIndicator.current) return
    swipeIndicator.current.style.opacity = '0'
    swipeIndicator.current.style.transform = 'translateY(-50%) translateX(-20px)'
    swipeIndicator.current.style.transition = 'transform 0.2s ease, opacity 0.2s ease'
    
    // Reset page position
    if (parallax) {
      document.body.style.transform = ''
      document.body.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.1, 0.25, 1)'
      if (pageOverlay.current) {
        pageOverlay.current.style.background = 'rgba(0, 0, 0, 0)'
      }
    }
    
    hasPassedThreshold.current = false
  }, [parallax])

  useEffect(() => {
    if (!enableBack || isDisabled) {
      return
    }

    createIndicator()

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      
      // For full screen mode, start swipe anywhere
      // For edge mode, only start if touch begins near left edge
      if (!fullScreen && touch.clientX > edgeWidth) {
        return
      }
      
      touchStartX.current = touch.clientX
      touchStartY.current = touch.clientY
      touchCurrentX.current = touch.clientX
      isSwiping.current = true
      hasPassedThreshold.current = false
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping.current) return
      
      const touch = e.touches[0]
      const deltaX = touch.clientX - touchStartX.current
      const deltaY = Math.abs(touch.clientY - touchStartY.current)
      
      // Cancel if too much vertical movement (user is scrolling)
      if (deltaY > maxVerticalMovement) {
        isSwiping.current = false
        hideIndicator()
        return
      }
      
      // For full-screen mode, require the swipe to be predominantly horizontal
      if (fullScreen && deltaY > Math.abs(deltaX) * 0.5) {
        isSwiping.current = false
        hideIndicator()
        return
      }
      
      // Only track rightward swipes (positive deltaX)
      if (deltaX > 0) {
        touchCurrentX.current = touch.clientX
        const progress = deltaX / threshold
        showIndicator(progress, deltaX)
      }
    }

    const handleTouchEnd = () => {
      if (!isSwiping.current) return
      
      const deltaX = touchCurrentX.current - touchStartX.current
      
      if (deltaX >= threshold) {
        handleBack()
      }
      
      isSwiping.current = false
      hideIndicator()
    }

    const handleTouchCancel = () => {
      isSwiping.current = false
      hideIndicator()
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: true })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
    document.addEventListener('touchcancel', handleTouchCancel, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchCancel)
      
      // Clean up indicator
      if (swipeIndicator.current) {
        swipeIndicator.current.remove()
        swipeIndicator.current = null
      }
      if (pageOverlay.current) {
        pageOverlay.current.remove()
        pageOverlay.current = null
      }
      // Reset body transform
      document.body.style.transform = ''
      document.body.style.transition = ''
    }
  }, [enableBack, isDisabled, threshold, maxVerticalMovement, edgeWidth, fullScreen, handleBack, createIndicator, showIndicator, hideIndicator])

  return {
    isSwipeEnabled: enableBack && !isDisabled,
  }
}

/**
 * Hook for swipe gestures on a specific element (e.g., modals, sheets)
 * Returns handlers to attach to the element
 */
export function useSwipeGesture(onSwipe: (direction: 'left' | 'right' | 'up' | 'down') => void, config: {
  threshold?: number
  direction?: 'horizontal' | 'vertical' | 'both'
} = {}) {
  const { threshold = 50, direction = 'horizontal' } = config
  
  const touchStart = useRef<{ x: number; y: number } | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return
    
    const touch = e.changedTouches[0]
    const deltaX = touch.clientX - touchStart.current.x
    const deltaY = touch.clientY - touchStart.current.y
    
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)
    
    if (direction === 'horizontal' || direction === 'both') {
      if (absX > threshold && absX > absY) {
        onSwipe(deltaX > 0 ? 'right' : 'left')
        hapticLight()
      }
    }
    
    if (direction === 'vertical' || direction === 'both') {
      if (absY > threshold && absY > absX) {
        onSwipe(deltaY > 0 ? 'down' : 'up')
        hapticLight()
      }
    }
    
    touchStart.current = null
  }, [threshold, direction, onSwipe])

  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
  }
}
